import { Button } from '@crowdex/ui/components/button';
import { CloudOff, LoaderCircle, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { readAppSettings, resolveDesktopTheme } from '@/lib/app-settings';

/**
 * The app could not load its session (the server is unreachable or answered with an error):
 * what happened in words, the error itself on demand, and Retry.
 */
export function BootstrapErrorScreen({
  details,
  retrying = false,
  onRetry,
}: {
  details: string;
  retrying?: boolean;
  onRetry: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  return (
    <main
      aria-labelledby="bootstrap-error-title"
      // `.desktop-shell` pads by the safe areas, over any padding of its own: the margins are the
      // section's.
      className="desktop-shell grid min-h-dvh place-items-center bg-background text-foreground"
      data-theme={resolveDesktopTheme(readAppSettings().theme)}
    >
      <section
        className="flex w-full max-w-sm flex-col items-center gap-4 px-6 py-12 text-center"
        role="alert"
      >
        <div
          aria-hidden
          className="flex size-12 items-center justify-center rounded-full bg-control text-muted-foreground"
        >
          <CloudOff className="size-5" />
        </div>
        <div className="space-y-1.5">
          <h1 className="type-page-title" id="bootstrap-error-title">
            Couldn't reach Crowdex
          </h1>
          <p className="type-meta text-muted-foreground">
            Check your connection, or that the Crowdex server is running, then try again.
          </p>
        </div>
        <div className="flex gap-2">
          <Button className="rounded-control px-4" disabled={retrying} onClick={onRetry}>
            {retrying ? (
              <LoaderCircle aria-hidden className="size-4 animate-spin" />
            ) : (
              <RotateCw aria-hidden className="size-4" />
            )}
            {retrying ? 'Retrying…' : 'Retry'}
          </Button>
          <Button
            aria-controls="bootstrap-error-details"
            aria-expanded={showDetails}
            className="rounded-control px-4"
            onClick={() => setShowDetails((open) => !open)}
            variant="outline"
          >
            Details
          </Button>
        </div>
        {showDetails && (
          <pre
            className="max-h-40 w-full overflow-auto whitespace-pre-wrap rounded-xl bg-code-surface px-3 py-2 text-left font-mono text-muted-foreground text-xs"
            id="bootstrap-error-details"
          >
            {details}
          </pre>
        )}
      </section>
    </main>
  );
}
